import { DemoError } from "./demo.js";
import { supabase } from "./supabase.js";

/**
 * The two sides of a team tie.
 *
 * A tie is the one format where the draw is not a function of the entry list alone: seed i
 * on one side plays seed i on the other, so who is on which side decides every fixture. That
 * makes the teams a thing an organiser picks and everybody can look at, rather than a
 * halfway state inside the draw generator — which is what they used to be.
 *
 * Reads go straight to `tie_teams` and `tie_roster`; both have had read policies for every
 * active member since the first migration. Writes go through two RPCs, because a tie is a
 * set of invariants — equal sides, nobody on both, everybody actually entered — and four
 * statements from a browser can leave any of them broken halfway.
 */

export interface TieMember {
  memberId: string;
  /** 1-based within the side, by rating. The database assigns it; see 20260810000600. */
  seed: number;
}

export interface TieTeam {
  id: string;
  side: "A" | "B";
  name: string;
  /** In seed order. */
  members: TieMember[];
}

interface TeamRow {
  id: string;
  side: "A" | "B";
  name: string;
  tie_roster: { member_id: string; seed: number }[];
}

/** Both sides, A first. Empty when the organiser has not picked them yet. */
export async function fetchTieTeams(eventId: string): Promise<TieTeam[]> {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from("tie_teams")
    .select("id, side, name, tie_roster (member_id, seed)")
    .eq("event_id", eventId)
    .order("side");

  if (error) throw new Error(error.message);

  return ((data ?? []) as unknown as TeamRow[]).map((row) => ({
    id: row.id,
    side: row.side,
    name: row.name,
    members: (row.tie_roster ?? [])
      .slice()
      .sort((a, b) => a.seed - b.seed)
      .map((entry) => ({ memberId: entry.member_id, seed: entry.seed })),
  }));
}

/**
 * Set the sides.
 *
 * **Array order is seed order.** Seed i plays seed i in a mirrored tie, so the order is the
 * fixture list rather than a way of displaying the roster — an organiser who wants their two
 * best players in the top duel has to be able to say so. An automatic split arrives here
 * already sorted by `splitIntoTeams`.
 */
export async function setTieTeams(
  eventId: string,
  sideA: string[],
  sideB: string[],
  names?: { a?: string | null; b?: string | null },
): Promise<number> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase.rpc("set_tie_teams", {
    target_event: eventId,
    side_a: sideA,
    side_b: sideB,
    name_a: names?.a ?? null,
    name_b: names?.b ?? null,
  });
  if (error) throw new Error(error.message);
  return data as number;
}
